import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, Landmark, QrCode, ShoppingBag, WalletCards } from "lucide-react";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { getPaymentMethodsByGroup } from "../../../../constant/paymentMethod";
import { useTheme } from "../../../CommonComponents/ThemeProvider";

const methodVisuals = {
    gcash: { short: "G", color: "#007dfe", icon: WalletCards },
    grabpay: { short: "G", color: "#00a650", icon: WalletCards },
    shopeepay: { short: "S", color: "#ee4d2d", icon: ShoppingBag },
    qrph: { short: "QR", color: "#1756a9", icon: QrCode },
    card: { short: "", color: "#334155", icon: CreditCard },
};

const MethodIcon = ({ method }) => {
    const visual = methodVisuals[method.key] || methodVisuals.card;
    const Icon = visual.icon;
    return (
        <span
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
            style={{ backgroundColor: visual.color }}
            aria-hidden="true"
        >
            {visual.short || <Icon size={15} />}
        </span>
    );
};

export default function PaymentPanel({
    paymentMethod,
    setPaymentMethod,
    configuredPaymentMethods = [],
    enabledPaymentMethods = [],
    paymentMode = "sandbox",
    collectionMode = "live",
}) {
    const { currentTheme } = useTheme();
    const configuredWallets = useMemo(
        () => getPaymentMethodsByGroup(configuredPaymentMethods, "wallet"),
        [configuredPaymentMethods]
    );
    const configuredBankCard = useMemo(
        () => getPaymentMethodsByGroup(configuredPaymentMethods, "bank_card"),
        [configuredPaymentMethods]
    );
    const enabledKeys = useMemo(
        () => new Set(enabledPaymentMethods.map((method) => String(method?.key || method))),
        [enabledPaymentMethods]
    );
    const [activeGroup, setActiveGroup] = useState("cash");

    useEffect(() => {
        if (paymentMethod === "cash") setActiveGroup("cash");
        else if (configuredWallets.some((method) => method.key === paymentMethod)) setActiveGroup("wallet");
        else if (configuredBankCard.some((method) => method.key === paymentMethod)) setActiveGroup("bank_card");
    }, [paymentMethod, configuredWallets, configuredBankCard]);

    const chooseGroup = (group) => {
        setActiveGroup(group);
        if (group === "cash") {
            setPaymentMethod("cash");
            return;
        }
        const methods = group === "wallet" ? configuredWallets : configuredBankCard;
        const firstEnabled = methods.find((method) => enabledKeys.has(method.key));
        setPaymentMethod(firstEnabled?.key || "");
    };

    const groups = [
        { key: "cash", label: "Cash", icon: HiOutlineCurrencyDollar, methods: [{ key: "cash" }] },
        { key: "wallet", label: "E-Wallet", icon: WalletCards, methods: configuredWallets },
        { key: "bank_card", label: "Bank / Card", icon: Landmark, methods: configuredBankCard },
    ].filter((group) => group.key === "cash" || group.methods.length > 0);
    const visibleMethods = activeGroup === "wallet" ? configuredWallets : configuredBankCard;

    return (
        <aside className="w-full rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Payment Method</h2>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${collectionMode === "manual" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {collectionMode === "manual" ? "Manual" : "Live"}
                </span>
            </div>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))` }}>
                {groups.map((group) => {
                    const Icon = group.icon;
                    const isActive = activeGroup === group.key;
                    return (
                        <button
                            key={group.key}
                            type="button"
                            onClick={() => chooseGroup(group.key)}
                            className={`flex h-10 min-w-0 items-center justify-center gap-1 rounded-md border px-1.5 text-[11px] font-semibold transition ${isActive ? "!text-white" : ""}`}
                            style={isActive
                                ? { backgroundColor: currentTheme.color, borderColor: currentTheme.color, color: "#fff" }
                                : { borderColor: "#dbe3ee", backgroundColor: "#f8fafc", color: "#475569" }}
                        >
                            <Icon size={15} className="shrink-0" style={{ color: isActive ? "#fff" : undefined }} />
                            <span className="truncate" style={{ color: isActive ? "#fff" : undefined }}>{group.label}</span>
                        </button>
                    );
                })}
            </div>

            {activeGroup !== "cash" && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                    <div className="grid grid-cols-2 gap-2">
                        {visibleMethods.map((method) => {
                            const available = enabledKeys.has(method.key);
                            const selected = paymentMethod === method.key;
                            return (
                                <button
                                    key={method.key}
                                    type="button"
                                    disabled={!available}
                                    onClick={() => setPaymentMethod(method.key)}
                                    className="flex h-12 min-w-0 items-center gap-2 rounded-md border px-2 text-left text-xs font-semibold transition disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                                    style={selected
                                        ? { borderColor: currentTheme.color, boxShadow: `0 0 0 1px ${currentTheme.color}`, color: "#0f172a" }
                                        : { borderColor: "#dbe3ee" }}
                                >
                                    <MethodIcon method={method} />
                                    <span className="min-w-0">
                                        <span className="block truncate">{method.label}</span>
                                        {!available && (
                                            <span className="block truncate text-[10px] font-medium text-amber-600">
                                                {paymentMode === "sandbox" ? "Production only" : "Unavailable"}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {paymentMethod === "qrph" && (
                        <div className="mt-2 rounded-md bg-slate-50 px-3 py-2">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Supported banking apps</span>
                            <div className="mt-1.5 flex gap-3">
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700"><span className="h-2 w-2 rounded-full bg-red-600" />BPI</span>
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700"><span className="h-2 w-2 rounded-full bg-blue-700" />UnionBank</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </aside>
    );
}
